import React, {
    ReactElement,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Button, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useDrop } from 'react-dnd'
import { ContentPath } from 'common/src/ContentPath'
import { FormulaSheetEntry } from 'common/src/formulaSheet/types'
import { DraggableSortingList } from '@/components/utils/DraggableSortingList/DraggableSortingList'
import {
    DraggableFormulaSheetEntryData,
    DraggableIconType,
} from '@/components/utils/DraggableSortingList/DraggableIcon/DraggableIcon.types'
import { DraggableItem } from '@/components/utils/DraggableItem/DraggableItem'
import { FormulaSheetAgentSurface } from './FormulaSheetAgentSurface'

interface FormulaSheetSectionGroupProps {
    contentPath: ContentPath | null
    entries: FormulaSheetEntry[]
    dirtyEntryIds: { has: (id: string) => boolean }
    isReadOnly: boolean
    isCourseFormulaJobActive: boolean
    onReorder: (orderedIds: string[]) => void
    stalePathByEntryId?: Map<string, string>
    listId: string
    onAdd: () => void
    onExternalDrop: (
        sourceListId: string,
        sourceIndex: number,
        beforeIndex: number
    ) => void
}

export const FormulaSheetSectionGroup: React.FC<
    FormulaSheetSectionGroupProps
> = (props): ReactElement => {
    const [activeIndex, setActiveIndex] = useState<number>(0)
    const items = useMemo<DraggableFormulaSheetEntryData[]>(
        () =>
            props.entries.map((entry) => ({
                type: DraggableIconType.formulaSheetEntry,
                title: entry.title,
                id: entry.id,
                hasUnsavedChanges: props.dirtyEntryIds.has(entry.id),
            })),
        [props.dirtyEntryIds, props.entries]
    )
    const reorder = useCallback(
        (startIndex: number, endIndex: number): void => {
            const ids = props.entries.map((entry) => entry.id)
            const [movedId] = ids.splice(startIndex, 1)
            ids.splice(endIndex, 0, movedId)
            props.onReorder(ids)
            setActiveIndex(endIndex)
        },
        [props]
    )
    // The whole open panel body accepts drops: rows handle precise
    // insert-before placement themselves (didDrop), anywhere else = end.
    // Props behind a ref so the spec never re-registers mid-drag
    // (re-registration ends the in-flight drag — see DraggableItem)
    const propsRef = useRef(props)
    propsRef.current = props
    const [{ isBodyOver }, bodyDrop] = useDrop<
        { index: number; listId?: string },
        unknown,
        { isBodyOver: boolean }
    >(
        () => ({
            accept: DraggableItem.name,
            collect: (monitor) => ({
                isBodyOver:
                    monitor.isOver({ shallow: true }) &&
                    monitor.getItem()?.listId !== propsRef.current.listId,
            }),
            drop: (item, monitor) => {
                const current = propsRef.current
                if (monitor.didDrop() || current.isReadOnly || !item.listId)
                    return
                if (item.listId === current.listId) {
                    const ids = current.entries.map((entry) => entry.id)
                    const [movedId] = ids.splice(item.index, 1)
                    ids.push(movedId)
                    current.onReorder(ids)
                    return
                }
                current.onExternalDrop(
                    item.listId,
                    item.index,
                    current.entries.length
                )
            },
        }),
        [props.listId]
    )
    return (
        <div
            ref={bodyDrop}
            style={
                isBodyOver
                    ? {
                          backgroundColor: 'rgba(24, 144, 255, 0.08)',
                          borderRadius: '4px',
                      }
                    : undefined
            }
        >
            <DraggableSortingList
                itemData={items}
                changeIndex={reorder}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                isLoading={false}
                isDisabled={props.isReadOnly}
                hasExternalDndProvider={true}
                listId={props.listId}
                onExternalDropAtIndex={(item, index) => {
                    if (!item.listId) return
                    props.onExternalDrop(item.listId, item.index, index)
                }}
            />
            {props.stalePathByEntryId &&
                props.entries.map((entry) => {
                    const stalePath = props.stalePathByEntryId?.get(entry.id)
                    return stalePath ? (
                        <Typography.Text
                            type={'secondary'}
                            key={entry.id}
                            style={{ display: 'block', fontSize: '0.75rem' }}
                        >
                            linked to deleted outline: {stalePath}
                        </Typography.Text>
                    ) : null
                })}
            <Button
                type={'dashed'}
                block
                icon={<PlusOutlined />}
                disabled={props.isReadOnly}
                onClick={props.onAdd}
                style={{ marginTop: '0.5rem' }}
            >
                Add entry
            </Button>
            {props.contentPath && (
                <FormulaSheetAgentSurface
                    contentPath={props.contentPath}
                    entryCount={props.entries.length}
                    isCourseFormulaJobActive={props.isCourseFormulaJobActive}
                />
            )}
        </div>
    )
}
